import React from "react";
import { ShieldCheck, Target, Eye, Heart } from "lucide-react";
import SEO, { breadcrumbSchema } from "@/components/SEO";

const VALUES = [
  { icon: ShieldCheck, title: "Reliability first", text: "Every vehicle is serviced before each rental. Transparent pricing — no hidden charges, ever." },
  { icon: Target, title: "Customer-first service", text: "From booking to drop-off, we make sure your experience is seamless." },
  { icon: Heart, title: "Affordable & flexible", text: "Daily, weekly and monthly rates designed for every budget — local and international." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Titan Car Rentals — Ghana's Premium Car Hire Company"
        description="Titan Car Rentals is Ghana's premium car rental brand — East Legon, Accra. Modern fleet, transparent pricing, international-standard service with Ghanaian hospitality. Our story, mission and team."
        path="/about"
        jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])}
      />
      <section className="bg-[#0d2040] text-white py-20">
        <div className="container-titan grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="eyebrow text-[#ff8a93]">About Titan</span>
            <h1 className="h-section mt-3 text-white">
              Reliable transportation you can trust.
            </h1>
            <p className="text-white/80 mt-5 leading-relaxed max-w-lg">
              Titan Car Rentals is an Accra-based car rental company providing professional self-drive and
              chauffeur-drive services across Ghana.
            </p>
            <p className="text-white/70 mt-4 leading-relaxed max-w-lg">
              We understand that mobility should be simple, safe, and affordable. That&apos;s why we serve both local
              residents and international hirers with a well-maintained fleet, transparent pricing, and customer-first
              service.
            </p>
          </div>
          <div className="aspect-[5/4] bg-[#0d2040]/40 overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1568025848823-86404cd04ad1?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
              alt="Accra cityscape"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-titan grid lg:grid-cols-2 gap-8 max-w-6xl">
          <div className="border border-gray-200 p-8 bg-white">
            <div className="w-11 h-11 bg-[#e63946] flex items-center justify-center">
              <Target size={20} className="text-white" />
            </div>
            <h2 className="font-display text-2xl font-bold text-[#0d2040] mt-5">Our Mission</h2>
            <p className="text-gray-700 mt-3 leading-relaxed">
              To provide safe, reliable, and budget-friendly car rental services in Accra through self-drive and
              chauffeur-drive options, ensuring convenience and value for every local and international client.
            </p>
          </div>
          <div className="border border-gray-200 p-8 bg-white">
            <div className="w-11 h-11 bg-[#0d2040] flex items-center justify-center">
              <Eye size={20} className="text-white" />
            </div>
            <h2 className="font-display text-2xl font-bold text-[#0d2040] mt-5">Our Vision</h2>
            <p className="text-gray-700 mt-3 leading-relaxed">
              To be the most trusted and preferred car rental company in Ghana, known for transparency, affordability,
              and exceptional customer service.
            </p>
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#f7f8fa]">
        <div className="container-titan">
          <div className="max-w-2xl">
            <span className="eyebrow">What we stand for</span>
            <h2 className="h-section mt-3 text-[#0d2040]">Built on trust. Driven by service.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            {VALUES.map((v) => (
              <div key={v.title} className="p-7 border border-gray-200 bg-white">
                <div className="w-11 h-11 bg-[#e63946] flex items-center justify-center">
                  <v.icon size={20} className="text-white" />
                </div>
                <h3 className="font-display text-lg font-semibold text-[#0d2040] mt-4">{v.title}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0d2040] text-white">
        <div className="container-titan grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="h-section text-white">
              Whether you&apos;re in Accra for business, tourism, airport transfers, or long-term hire —
              <span className="text-[#ff8a93]"> we&apos;ve got you covered.</span>
            </h2>
            <p className="text-white/70 mt-5 leading-relaxed max-w-lg">
              We offer flexible rental options designed to fit your budget and schedule. From booking to drop-off, our
              team is committed to making your experience seamless.
            </p>
            <p className="text-white/80 mt-4 leading-relaxed max-w-lg font-medium">
              At Titan Car Rentals, we don&apos;t just rent cars — we provide reliable transportation you can trust.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/5 border border-white/10 p-6 text-center">
              <div className="stat-number">9+</div>
              <div className="text-xs uppercase tracking-widest text-white/70 mt-2">Vehicle models</div>
            </div>
            <div className="bg-white/5 border border-white/10 p-6 text-center">
              <div className="stat-number">3</div>
              <div className="text-xs uppercase tracking-widest text-white/70 mt-2">Core services</div>
            </div>
            <div className="bg-white/5 border border-white/10 p-6 text-center">
              <div className="stat-number">7+</div>
              <div className="text-xs uppercase tracking-widest text-white/70 mt-2">Pickup zones</div>
            </div>
            <div className="bg-white/5 border border-white/10 p-6 text-center">
              <div className="stat-number">24/7</div>
              <div className="text-xs uppercase tracking-widest text-white/70 mt-2">Support</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
